import Axios from '../../utils/axios';
import { getBackendErrorMessage } from '../../utils/errorHandler';

export interface Vehicle {
  id: number;
  ID?: number; // API returns ID (uppercase) instead of id
  platNomor: string;
  vin: string;
  imei: string | null;
  modelID: string;
  oemName: string;
  modelName: string;
  color: string;
  status: string;
  stnkLink?: string | null;
  stnkAnnualExpiryDate?: string | null;
  stnkAnnualFileUrl?: string | null;
  stnkFiveYearExpiryDate?: string | null;
  stnkFiveYearFileUrl?: string | null;
  appUsername: string;
  appPassword: string;
  unitEquipment: string[];
  vehicleImageURL: string;
  contractDuration?: number;
  contractStartDate?: string;
  contractEndDate?: string;
  arriveAt?: string | null;
  // Idle tracking — populated for IDLE vehicles only, null otherwise
  modelPurchasePrice?: number;
  idleSince?: string | null;
  idleDays?: number | null;
  idleCost?: number | null;
  // Set when the OEM deal covering this unit is paid off — idleCost is null
  costSettledAt?: string | null;
  // Latest telemetry snapshot — nulls until the unit reports in. SOC,
  // odometer and range come back as raw vendor strings, not numbers.
  telemetryLatitude?: number | null;
  telemetryLongitude?: number | null;
  telemetrySpeed?: number | null;
  telemetryIgnition?: boolean | null;
  telemetryBms1Soc?: string | null;
  telemetryBms2Soc?: string | null;
  telemetryOdometer?: string | null;
  telemetryRange?: string | null;
  telemetryLastAt?: string | null;
  linkedIncidentType?: string;
  hubId?: string;
  hubName?: string;
  businessUnit?: string;
  entity?: string;
  nomorMesin?: string;
  purchaseOrderID?: string;
  purchaseOrderCode?: string;
  notes: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface VehicleChangeEntry {
  ID?: string;
  vehicleID?: string;
  reason?: string | null;
  before: Record<string, any>;
  after: Record<string, any>;
  createdBy: string;
  createdAt: string;
}

export interface VehicleCreateRequest {
  platNomor: string;
  vin: string;
  imei?: string;
  modelID: string;
  color: string;
  status: string;
  stnkLink?: string;
  stnkAnnualExpiryDate?: string;
  stnkAnnualFileUrl?: string;
  stnkFiveYearExpiryDate?: string;
  stnkFiveYearFileUrl?: string;
  appUsername: string;
  appPassword: string;
  unitEquipment: string[];
  vehicleImageURL: string;
  notes: string;
  arriveAt: string;
  entity?: string;
  businessUnit?: string;
  purchaseOrderID?: string;
  createdBy: string;
}

export interface VehicleUpdateRequest extends Partial<VehicleCreateRequest> {
  /** Required by PATCH /v1/vehicles/:id — who made the edit. */
  updatedBy?: string;
  /**
   * Justification stored on the vehicle change log row. Mandatory when the
   * edit moves the unit's entity or business unit.
   */
  vehicleChangesReason?: string;
}

export interface VehicleReturnRequest {
  returnDate: string;
  returnOdometer?: number;
  returnCondition: 'GOOD' | 'MINOR_DAMAGE' | 'MAJOR_DAMAGE';
  returnReason:
    | 'CONTRACT_END'
    | 'IDLE_AT_HUB_TOO_LONG'
    | 'VEHICLE_SWAP'
    | 'CLIENT_REQUEST'
    | 'OTHER';
  returnReasonOther?: string;
  vehicleReturnImageURL?: string;
  unitEquipment?: string[];
  returnUnitEquipment?: string[];
  updatedBy: string;
  returnBastURL?: string;
  returnOemRepName?: string;
  returnOemRepPhone?: string;
  returnNotes?: string;
}

export interface VehicleListResponse {
  data: Vehicle[];
  pagination: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  };
}

export interface VehicleResponse {
  vehicle: Vehicle;
}

export const vehiclesApi = {
  getVehicles: async (
    page = 1,
    size = 10,
    search?: string,
    filters?: Record<string, any>
  ): Promise<VehicleListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (search) {
      params.append('search', search);
    }

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value) && value.length > 0) {
            // Convert array to comma-separated string for filters like oemIDs, modelIDs, statuses
            if (
              key.endsWith('IDs') ||
              key.endsWith('statuses') ||
              key === 'contractTypes'
            ) {
              params.append(key, value.join(','));
            } else {
              value.forEach((v) => params.append(`${key}[]`, v));
            }
          } else if (!Array.isArray(value)) {
            params.append(key, value.toString());
          }
        }
      });
    }

    try {
      const response = await Axios.get(`/v1/vehicles?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      const backendMessage = getBackendErrorMessage(error);
      throw new Error(backendMessage);
    }
  },

  createVehicle: async (
    vehicleData: VehicleCreateRequest
  ): Promise<VehicleResponse> => {
    try {
      const response = await Axios.post('/v1/vehicles', vehicleData);
      return response.data;
    } catch (error: any) {
      const backendMessage = getBackendErrorMessage(error);
      throw new Error(backendMessage);
    }
  },

  updateVehicle: async (
    id: number,
    vehicleData: VehicleUpdateRequest
  ): Promise<VehicleResponse> => {
    try {
      const response = await Axios.patch(`/v1/vehicles/${id}`, vehicleData);
      return response.data;
    } catch (error: any) {
      const backendMessage = getBackendErrorMessage(error);
      throw new Error(backendMessage);
    }
  },

  deleteVehicle: async (id: number, deletedBy?: string): Promise<void> => {
    try {
      const data = deletedBy ? { deletedBy } : {};
      await Axios.delete(`/v1/vehicles/${id}`, { data });
    } catch (error: any) {
      const backendMessage = getBackendErrorMessage(error);
      throw new Error(backendMessage);
    }
  },

  getVehicle: async (id: string): Promise<VehicleResponse> => {
    try {
      const response = await Axios.get(`/v1/vehicles/${id}`);
      return response.data;
    } catch (error: any) {
      const backendMessage = getBackendErrorMessage(error);
      throw new Error(backendMessage);
    }
  },

  // settled: true → this unit stops accruing idle cost (OEM deal paid off);
  // false → resume accrual.
  settleIdleCost: async (
    id: string,
    data: { settled: boolean; updatedBy: string }
  ): Promise<void> => {
    try {
      await Axios.patch(`/v1/vehicles/${id}/settle-idle-cost`, data);
    } catch (error: any) {
      const backendMessage = getBackendErrorMessage(error);
      throw new Error(backendMessage);
    }
  },

  getVehicleChanges: async (id: string): Promise<VehicleChangeEntry[]> => {
    try {
      const response = await Axios.get(`/v1/vehicles/${id}/changes`);
      return response.data?.data || response.data || [];
    } catch (error: any) {
      const backendMessage = getBackendErrorMessage(error);
      throw new Error(backendMessage);
    }
  },

  returnVehicle: async (
    id: number,
    returnData: VehicleReturnRequest
  ): Promise<VehicleResponse> => {
    try {
      const response = await Axios.patch(
        `/v1/vehicles/${id}/return`,
        returnData
      );
      return response.data;
    } catch (error: any) {
      const backendMessage = getBackendErrorMessage(error);
      throw new Error(backendMessage);
    }
  },

  editReturnVehicle: async (
    id: number,
    returnData: VehicleReturnRequest
  ): Promise<VehicleResponse> => {
    try {
      const response = await Axios.patch(
        `/v1/vehicles/return/${id}`,
        returnData
      );
      return response.data;
    } catch (error: any) {
      const backendMessage = getBackendErrorMessage(error);
      throw new Error(backendMessage);
    }
  },

  downloadOwnedCsv: async (params?: Record<string, any>): Promise<Blob> => {
    try {
      const response = await Axios.get('/v1/vehicles/owned/download/csv', {
        params,
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      const backendMessage = getBackendErrorMessage(error);
      throw new Error(backendMessage);
    }
  },

  downloadReturnedCsv: async (params?: Record<string, any>): Promise<Blob> => {
    try {
      const response = await Axios.get('/v1/vehicles/returned/download/csv', {
        params,
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      const backendMessage = getBackendErrorMessage(error);
      throw new Error(backendMessage);
    }
  },
};
